if (!customElements.get('wls-slider')) {
  class WLSSlider extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        slider: 'js-wls-slider',
        nextButton: 'js-wls-slider-next',
        prevButton: 'js-wls-slider-prev',
        sliderSettings: `SliderSettings-${this.getAttribute('data-id')}`,
        pagination: 'js-wls-slider-pagination',
        navigation: 'js-slider-navigation',
        swiperSlide: 'swiper-slide',
        video: 'wls-video',
        playing: 'playing',
        hidden: 'hidden',
        locked: 'wls-slider--locked',
        fallback: 'wls-slider--fallback',
      };

      this.selectors = this.getSelectors();

      this.swiperInstance = null;
    }

    /**
     * @returns {Object}
     * @description Get selectors
     */
    getSelectors() {
      return {
        slider: this.querySelector(`.${this.classes.slider}`),
        nextButtons: this.querySelectorAll(`.${this.classes.nextButton}`),
        prevButtons: this.querySelectorAll(`.${this.classes.prevButton}`),
        paginations: this.querySelectorAll(`.${this.classes.pagination}`),
        navigations: this.querySelectorAll(`.${this.classes.navigation}`),
        slides: this.querySelectorAll(`.${this.classes.swiperSlide}`),
      };
    }

    /**
     * @description Initialize the slider
     */
    connectedCallback() {
      this.init();
      window.addEventListener('resize', this.handleResize);
    }

    /**
     * @description Destroy the slider
     */
    disconnectedCallback() {
      this.destroy();
      this.classList.remove(this.classes.fallback);
      this.style.removeProperty('--fallback-slides-per-view');
      this.style.removeProperty('--fallback-gap');
      window.removeEventListener('resize', this.handleResize);
    }

    /**
     * @description Initialize the slider
     */
    init() {
      const totalSlides = this.selectors.slides?.length || 0;

      const {
        customSettings,
        slidesPerView,
        loop,
        centeredSlides,
        spaceBetween,
        navigation,
        pagination,
        autoplay,
        insufficientSlidesPosition,
      } = this.getSliderSettingsFromLiquid();

      if (this.shouldUseFallbackLayout(slidesPerView, totalSlides)) {
        this.applyFallbackLayout(slidesPerView, spaceBetween);
        return;
      }

      const shouldCenterSlides = {
        base: totalSlides > slidesPerView.base + 2,
        tablet: totalSlides >= slidesPerView.tablet + 2,
        desktop: totalSlides >= slidesPerView.desktop + 2,
      };

      const resolveLoop = (configValue, slidesPerViewValue) => {
        if (slidesPerViewValue === 'auto') return configValue;
        return configValue ? totalSlides > slidesPerViewValue : false;
      };

      this.swiperInstance = new Swiper(this.selectors.slider, {
        slidesPerView: slidesPerView.base ?? 1.3,
        spaceBetween: spaceBetween.base ?? 16,
        loop: resolveLoop(loop.base, slidesPerView.base ?? 1.3),
        centeredSlides: centeredSlides.base ? shouldCenterSlides.base : false,
        ...customSettings,
        centerInsufficientSlides: insufficientSlidesPosition.base === 'center' ? true : false,
        navigation: {
          nextEl: [...this.selectors.nextButtons],
          prevEl: [...this.selectors.prevButtons],
          enabled: navigation?.base ?? true,
        },
        pagination: {
          el: [...this.selectors.paginations],
          clickable: true,
          enabled: pagination?.base ?? true,
        },
        autoplay: {
          enabled: autoplay.base ?? false,
          delay: autoplay.time ?? 3000,
        },
        on: {
          slideChange: this.handleSlideChange.bind(this),
          init: (swiper) => {
            this.selectors.navigations.forEach((navigation) => {
              navigation.classList.remove(this.classes.hidden);
            });
          },
          lock: () => {
            this.classList.add(this.classes.locked);
          },
          unlock: () => {
            this.classList.remove(this.classes.locked);
          },
        },
        breakpoints: {
          768: {
            slidesPerView: slidesPerView.tablet ?? 2,
            spaceBetween: spaceBetween.tablet ?? 16,
            loop: resolveLoop(loop.tablet, slidesPerView.tablet ?? 2),
            centeredSlides: centeredSlides.tablet ? shouldCenterSlides.tablet : false,
            centerInsufficientSlides: insufficientSlidesPosition.tablet === 'center' ? true : false,
            navigation: {
              enabled: navigation?.tablet ?? true,
            },
            pagination: {
              enabled: pagination?.tablet ?? true,
            },
          },
          1024: {
            slidesPerView: slidesPerView.desktop ?? 4,
            spaceBetween: spaceBetween.desktop ?? 24,
            loop: resolveLoop(loop.desktop, slidesPerView.desktop ?? 4),
            centeredSlides: centeredSlides.desktop ? shouldCenterSlides.desktop : false,
            centerInsufficientSlides: insufficientSlidesPosition.desktop === 'center' ? true : false,
            navigation: {
              enabled: navigation?.desktop ?? true,
            },
            pagination: {
              enabled: pagination?.desktop ?? true,
            },
          },
        },
      });
    }

    /**
     * @description Handle slide change event by pausing all videos
     */
    handleSlideChange() {
      const videos = this.querySelectorAll(`.${this.classes.video}`);
      videos.forEach((videoElement) => {
        const video = videoElement.querySelector('video');
        if (video && !video.hasAttribute('autoplay') && !video.paused) {
          videoElement.classList.remove(this.classes.playing);
          video.pause();
        }
      });
    }

    /**
     * @description Handle resize event and apply fallback layout when there are not enough slides
     */
    handleResize = () => {
      const totalSlides = this.selectors.slides?.length || 0;
      const { slidesPerView, spaceBetween } = this.getSliderSettingsFromLiquid();

      const enoughSlides = !this.shouldUseFallbackLayout(slidesPerView, totalSlides);

      const isSwiperActive = !!this.swiperInstance && !this.swiperInstance.destroyed;

      if (enoughSlides && !isSwiperActive) {
        this.classList.remove(this.classes.fallback);
        this.style.removeProperty('--fallback-slides-per-view');
        this.style.removeProperty('--fallback-gap');
        this.init();
        return;
      }

      if (!enoughSlides && isSwiperActive) {
        this.destroy();
        this.applyFallbackLayout(slidesPerView, spaceBetween);
        return;
      }

      if (!enoughSlides) {
        this.applyFallbackLayout(slidesPerView, spaceBetween);
      }
    };

    /**
     * @description Apply fallback layout when there are not enough slides
     * @param {Object} slidesPerView
     * @param {Object} spaceBetween
     */
    applyFallbackLayout(slidesPerView, spaceBetween) {
      const currentBreakpoint = window.innerWidth;

      let activeSlidesPerView = slidesPerView.base ?? 1.3;
      let gap = spaceBetween.base ?? 16;

      if (currentBreakpoint >= 1024) {
        activeSlidesPerView = slidesPerView.desktop ?? activeSlidesPerView;
        gap = spaceBetween.desktop ?? gap;
      } else if (currentBreakpoint >= 768) {
        activeSlidesPerView = slidesPerView.tablet ?? activeSlidesPerView;
        gap = spaceBetween.tablet ?? gap;
      }

      this.classList.add(this.classes.fallback);
      this.style.setProperty('--fallback-slides-per-view', activeSlidesPerView);
      this.style.setProperty('--fallback-gap', `${gap}px`);

      this.selectors.navigations.forEach((nav) => {
        nav.classList.add(this.classes.hidden);
      });
    }

    /**
     * @description Check if fallback layout should be used based on total slides and slidesPerView
     * @param {Object} slidesPerView
     * @param {Number} totalSlides
     * @returns {Boolean}
     */
    shouldUseFallbackLayout(slidesPerView, totalSlides) {
      const currentWidth = window.innerWidth;

      let spv = slidesPerView.base ?? 1.3;
      if (currentWidth >= 1024) spv = slidesPerView.desktop ?? spv;
      else if (currentWidth >= 768) spv = slidesPerView.tablet ?? spv;

      return spv === 'auto' ? totalSlides <= 1 : totalSlides <= parseFloat(spv);
    }

    /**
     * @description Destroy the slider
     */
    destroy() {
      this.swiperInstance.destroy();
    }

    /**
     * @returns {Object}
     * @description Get slider settings from liquid
     */
    getSliderSettingsFromLiquid() {
      const settingsElement = document.getElementById(`${this.classes.sliderSettings}`);
      const settings = JSON.parse(settingsElement.textContent);
      return settings;
    }
  }
  customElements.define('wls-slider', WLSSlider);
}
